package com.iregained.site.components.sections.corporate.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.corporate.styles.PolicyBodyTextStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Subtle Rise
@Composable
private fun Modifier.subtleRise(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun CorporatePolicySection(data: CorporatePolicySectionData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "policy-section"
    var isVisible by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
            .padding(topBottom = if (isDesktop) 16.px else 12.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1000.px), horizontalAlignment = Alignment.Start
            ) {
                if (data.headerTitle.isNotBlank()) {
                    SectionHeader(
                        text = data.headerTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 15.percent,
                        modifier = Modifier.margin(bottom = 12.px).subtleRise(isVisible, 0),
                        alignment = Alignment.Start
                    )
                }

                if (data.metaDetails.isNotBlank()) {
                    Div(
                        attrs = Modifier.fontSize(14.px).color(palette.brand.primary).fontWeight(FontWeight.Bold)
                            .subtleRise(isVisible, 150).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.metaDetails
                                    onDispose {}
                                }
                            }
                    )
                }

                if (data.bodyHtml.isNotBlank()) {
                    Div(
                        attrs = PolicyBodyTextStyle.toModifier().color(palette.custom.darkText).fontSize(16.px)
                            .lineHeight(1.6).subtleRise(isVisible, 300).toAttrs {
                            ref { el -> el.innerHTML = data.bodyHtml; onDispose {} }
                        })
                }
            }
        }
    }
}

data class CorporatePolicySectionData(
    val sectionId: String? = null, val headerTitle: String, val metaDetails: String = "", val bodyHtml: String
)